import { useState } from 'react';
import {
  usePosts,
  useDeletePost,
  usePendingPostsCount,
} from '@/lib/hooks/usePosts';
import { useAuth } from '@/lib/hooks/useAuth';
import { useUserProfile } from '@/lib/hooks/useUserProfile';
import { Post } from '@/components/community/Post';
import { PostListSkeleton } from '@/components/community/PostListSkeleton';
import { CreatePostDrawer } from '@/components/community/CreatePostDrawer';
import { PostActionsDrawer } from '@/components/community/PostActionsDrawer';
import { EditPostDrawer } from '@/components/community/EditPostDrawer';
import { ActionDrawer } from '@/components/ui/action-drawer';
import { ToastNotification } from '@/components/ui/ToastNotification';
import { useToastNotification } from '@/lib/hooks/useToastNotification';
import { formatDateTimeForList } from '@/lib/utils/date.utils';
import {
  getVisibleModerationStatusForViewer,
  getVisiblePostsForViewer,
} from '@/lib/utils/posts.utils';
import type { PostWithAuthor } from '@/lib/types/database';
import { PlusIcon, ArrowRight } from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';

export function Feed() {
  const { user } = useAuth();
  const { isAdmin } = useUserProfile();
  const navigate = useNavigate();
  const { data: posts, isLoading, error } = usePosts();
  const deletePost = useDeletePost();
  const { toast, showToast, hideToast } = useToastNotification();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isActionsDrawerOpen, setIsActionsDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<PostWithAuthor | null>(null);

  const userIsAdmin = isAdmin();
  const { data: pendingCount = 0 } = usePendingPostsCount(userIsAdmin);
  const visiblePosts = getVisiblePostsForViewer({
    posts,
    userId: user?.id,
    isAdmin: userIsAdmin,
  });

  if (isLoading) {
    return (
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="mb-6">Community</h1>
        <PostListSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="mb-6">Community</h1>
        <p className="text-body text-red-400">
          Failed to load posts. Please try again later.
        </p>
      </div>
    );
  }

  if (visiblePosts.length === 0) {
    return (
      <>
        <div className="flex justify-center items-center flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
          <h1 className="mb-6">Community</h1>
          {userIsAdmin && pendingCount > 0 && (
            <button
              onClick={() => navigate({ to: '/admin/moderate-posts' })}
              className="w-full bg-yellow-500/20 border border-yellow-500/50 py-3 px-4 rounded-lg mb-4 flex items-center justify-between gap-2 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
            >
              <span className="font-medium">
                {pendingCount} Post{pendingCount !== 1 ? 's' : ''} Waiting for
                Approval
              </span>
              <ArrowRight size={20} weight="bold" />
            </button>
          )}
          <button
            className="w-full bg-sky-500 py-4 px-4 rounded-lg mb-7 flex items-center justify-between gap-2"
            onClick={() => setIsDrawerOpen(true)}
          >
            What&apos;s on your mind?
            <PlusIcon size={20} />
          </button>
          <p className="text-body">
            No posts yet. Be the first to share something!
          </p>
        </div>
        <CreatePostDrawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen} />
      </>
    );
  }

  const handleMenuClick = (post: PostWithAuthor) => {
    setSelectedPost(post);
    setIsActionsDrawerOpen(true);
  };

  const handleEdit = () => {
    setIsActionsDrawerOpen(false);
    setIsEditDrawerOpen(true);
  };

  const handleDeleteClick = () => {
    setIsActionsDrawerOpen(false);
    setIsDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedPost) {
      return;
    }

    try {
      await deletePost.mutateAsync(selectedPost.id);
      setIsDeleteConfirmOpen(false);
      setSelectedPost(null);
      // Show success toast
      showToast({
        title: 'Post deleted',
        description: 'Your post has been deleted successfully.',
        variant: 'success',
      });
    } catch (error) {
      // Show error toast
      showToast({
        title: 'Delete failed',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to delete post. Please try again.',
        variant: 'error',
      });
      console.error('Failed to delete post:', error);
    }
  };

  const handleCloseActionsDrawer = (open: boolean) => {
    setIsActionsDrawerOpen(open);
    if (!open) {
      setSelectedPost(null);
    }
  };

  const handleCloseEditDrawer = (open: boolean) => {
    setIsEditDrawerOpen(open);
    if (!open) {
      setSelectedPost(null);
    }
  };

  const handleCloseDeleteConfirm = (open: boolean) => {
    setIsDeleteConfirmOpen(open);
    if (!open) {
      setSelectedPost(null);
    }
  };

  return (
    <>
      <div className="flex flex-col w-full max-w-4xl mx-auto px-4 pb-4 mt-[34px]">
        <h1 className="mb-6">Community</h1>

        {/* Admin banner for pending posts */}
        {userIsAdmin && pendingCount > 0 && (
          <button
            onClick={() => navigate({ to: '/admin/moderate-posts' })}
            className="w-full bg-yellow-500/20 border border-yellow-500/50 py-3 px-4 rounded-lg mb-4 flex items-center justify-between gap-2 text-yellow-400 hover:bg-yellow-500/30 transition-colors"
          >
            <span className="font-medium">
              {pendingCount} Post{pendingCount !== 1 ? 's' : ''} Waiting for
              Approval
            </span>
            <ArrowRight size={20} weight="bold" />
          </button>
        )}

        <button
          className="w-full bg-sky-500 py-4 px-4 rounded-lg mb-7 flex items-center justify-between gap-2"
          onClick={() => setIsDrawerOpen(true)}
        >
          What&apos;s on your mind?
          <PlusIcon size={20} />
        </button>

        <div className="divide-y divide-slate-600 flex flex-col gap-4">
          {visiblePosts.map((post) => {
            const isOwner = post.user_id === user?.id;
            const isAdminOrOwner = userIsAdmin || isOwner;
            const authorName = post.author_name || 'Unknown User';
            const formattedTimestamp = formatDateTimeForList(post.created_at);

            return (
              <Post
                key={post.id}
                postId={post.id}
                author={authorName}
                disableClick={false}
                postTimestamp={formattedTimestamp}
                imgUrl={post.image_url || undefined}
                content={post.content}
                commentCount={post.comment_count}
                isAdminOrOwner={isAdminOrOwner}
                onMenuClick={() => handleMenuClick(post)}
                moderationStatus={getVisibleModerationStatusForViewer(
                  post,
                  user?.id,
                  userIsAdmin
                )}
              />
            );
          })}
        </div>
      </div>
      <CreatePostDrawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen} />
      <PostActionsDrawer
        open={isActionsDrawerOpen}
        onOpenChange={handleCloseActionsDrawer}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isDeleting={deletePost.isPending}
      />
      {selectedPost && (
        <>
          <EditPostDrawer
            open={isEditDrawerOpen}
            onOpenChange={handleCloseEditDrawer}
            postId={selectedPost.id}
            initialContent={selectedPost.content}
          />
          <ActionDrawer
            open={isDeleteConfirmOpen}
            onOpenChange={handleCloseDeleteConfirm}
            title="Delete Post?"
            description="This post will be permanently deleted. This action cannot be undone."
            confirmLabel="Delete"
            cancelLabel="Cancel"
            confirmVariant="destructive"
            onConfirm={handleConfirmDelete}
            isLoading={deletePost.isPending}
            loadingLabel="Deleting..."
          />
        </>
      )}

      {/* Toast Notification */}
      <ToastNotification
        open={toast.open}
        onClose={hideToast}
        title={toast.title}
        description={toast.description}
        variant={toast.variant}
        position="bottom"
      />
    </>
  );
}
